import { useCallback } from 'react';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import type { ExtractInjectionAPI, TOOLBAR_MENU_TYPE } from '@atlaskit/editor-common/types';

import type { InsertBlockPlugin } from '../../../insertBlockPluginType';
import { usePopupManager } from './usePopupManager';

interface UseTableSelectorPopupProps {
	api?: ExtractInjectionAPI<InsertBlockPlugin>;
	buttonRef: React.RefObject<HTMLElement>;
}

export const useTableSelectorPopup = ({
	api,
	buttonRef,
}: UseTableSelectorPopupProps): {
	handleSelectedTableSize: (rowsCount: number, colsCount: number) => void;
	onPopupUnmount: () => void;
	isOpen: boolean;
	isOpenedByKeyboard: boolean;
	toggle: (inputMethod?: TOOLBAR_MENU_TYPE | INPUT_METHOD) => void;
	close: () => void;
	handleEscapeKeydown: () => void;
	handleClickOutside: (e: MouseEvent) => void;
	handleKeyboardOpen: (event: React.KeyboardEvent) => void;
} => {
	const popupManager = usePopupManager({
		focusTarget: buttonRef,
	});

	const handleSelectedTableSize = useCallback(
		(rowsCount: number, colsCount: number): void => {
			// workaround to solve race condition where cursor is not placed correctly inside table
			queueMicrotask(() => {
				api?.core?.actions.execute(
					api?.table?.commands.insertTableWithSize(rowsCount, colsCount, INPUT_METHOD.PICKER),
				);
			});
			popupManager.close();
		},
		[api, popupManager],
	);

	const onPopupUnmount = useCallback((): void => {
		api?.core.actions.focus();
	}, [api]);

	return {
		...popupManager,
		handleSelectedTableSize,
		onPopupUnmount,
	};
};
